import { useCallback, useEffect, useRef, useState } from 'react';
import { updateCardInTrip } from '../../firebase/trips.utils';
import { useAppDispatch } from '../../redux/hooks';
import { updateCardPhotoReferences } from '../../redux/trip/trip.slice';
import { TripCard, TripID } from '../../redux/trip/trip.types';
import { invalidateCachedPlacePhotos } from '../../utils/photo-cache.utils';
import {
  extractPhotoReferences,
  getDetails,
  invalidatePlacePhotoCache,
} from '../../utils/places-api.utils';
import { usePlacePhotoUrls } from '../usePlacePhotoUrls';

/** The cover entry as a one-item array, falling back to the first if the index is stale. */
function pickCover(items: string[] | undefined, defaultImage: number | null | undefined) {
  if (!items?.length) {
    return items;
  }
  return [items[defaultImage ?? 0] ?? items[0]];
}

// Module-level set to prevent concurrent duplicate fetches for the same place.
// Shared across all hook instances so Card.tsx + CardDetailsModal don't race.
const inFlightFetches = new Set<string>();

/**
 * Resolves a card's photos, fetching and persisting photoReferences first if
 * the card has none. With `coverOnly`, `imgUrls` holds just the cover photo
 * (`card.defaultImage`) so a board of cards costs one gateway call per card
 * rather than one per photo.
 */
export function usePlaceImgUrlsForCard(
  tripId: TripID | undefined,
  card: TripCard,
  shouldLoad = true,
  { coverOnly = false }: { coverOnly?: boolean } = {}
): { imgUrls: string[]; isLoading: boolean; onImgError: () => void } {
  const dispatch = useAppDispatch();
  const [isFetchingReferences, setIsFetchingReferences] = useState(false);
  const [forceRefetch, setForceRefetch] = useState(false);
  // Prevent re-triggering after one refetch attempt per mount
  const hasAttemptedRefetch = useRef(false);

  // Primitive destructuring keeps the effect stable — only re-fires when photos are genuinely updated.
  const { id, googlePlaceId, photoReferences, imgUrls: legacyImgUrls } = card;

  const { urls, isLoading: isResolvingUrls } = usePlacePhotoUrls(
    coverOnly ? pickCover(photoReferences, card.defaultImage) : photoReferences,
    coverOnly ? pickCover(legacyImgUrls, card.defaultImage) : legacyImgUrls,
    shouldLoad
  );

  const onImgError = useCallback(() => {
    if (hasAttemptedRefetch.current || inFlightFetches.has(googlePlaceId)) {
      return;
    }
    invalidatePlacePhotoCache(googlePlaceId);
    invalidateCachedPlacePhotos(photoReferences);
    hasAttemptedRefetch.current = true;
    setForceRefetch(true);
  }, [googlePlaceId, photoReferences]);

  useEffect(() => {
    async function fetchAndPersistPhotoReferences() {
      if (!tripId || !shouldLoad) {
        return;
      }
      // Already on the new scheme and nothing reported broken — nothing to do.
      if (photoReferences?.length && !forceRefetch) {
        return;
      }
      // Legacy card with (presumably) working imgUrls and no reported error —
      // leave it alone. Backfilling old cards is MIG-05's job, not this hook's.
      if (legacyImgUrls?.length && !forceRefetch) {
        return;
      }

      if (inFlightFetches.has(googlePlaceId)) {
        return;
      }

      inFlightFetches.add(googlePlaceId);
      setIsFetchingReferences(true);

      try {
        const details = await getDetails({ placeId: googlePlaceId, fields: ['photos'] });
        const refs = extractPhotoReferences(details);

        if (refs.length > 0) {
          dispatch(updateCardPhotoReferences({ tripId, cardId: id, photoReferences: refs }));
          await updateCardInTrip(tripId, id, 'photoReferences', refs);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // biome-ignore lint/suspicious/noConsole: should remove these
          console.error(`Failed to fetch photos for card ${id}:`, error);
        }
      } finally {
        inFlightFetches.delete(googlePlaceId);
        setIsFetchingReferences(false);
        setForceRefetch(false);
      }
    }

    fetchAndPersistPhotoReferences();
  }, [
    tripId,
    id,
    googlePlaceId,
    photoReferences,
    legacyImgUrls,
    shouldLoad,
    forceRefetch,
    dispatch,
  ]);

  return { imgUrls: urls, isLoading: isResolvingUrls || isFetchingReferences, onImgError };
}
